import React, { useMemo, useState } from 'react';
import { Button, Space, Table, Tag, message } from 'antd';
import type { TableColumnsType } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { formatISODateTime } from 'utils/dateTimeUtils';
import EditableDrawer from '../../../../../components/EditableDrawer';
import {
  GET_WORK_PROGRESS_CLIENT_CARTONS,
  GetWorkProgressClientCartonsResponse,
} from '../../graphql/queries/getWorkProgressClientCartons';
import {
  GET_WORK_PROGRESS_BAG_PARCEL_DETAILS,
  GetWorkProgressBagParcelDetailsResponse,
  WorkProgressBagParcelDetail,
} from '../../graphql/queries/getWorkProgressBagParcelDetails';
import {
  GENERATE_WORK_PROGRESS_BAG_PARCEL_DOWNLOAD_REPORT,
  GenerateWorkProgressBagParcelDownloadReportResponse,
  GenerateWorkProgressBagParcelDownloadReportVariables,
  WorkProgressBagParcelExportFilterInput,
} from '../../graphql/mutations/generateWorkProgressBagParcelDownloadReport';
import {
  GENERATE_WORK_PROGRESS_BAG_DETAIL_DRILLDOWN_DOWNLOAD_REPORT,
  GenerateWorkProgressBagDetailDrilldownDownloadReportResponse,
  GenerateWorkProgressBagDetailDrilldownDownloadReportVariables,
  WorkProgressBagDetailDrilldownExportFilterInput,
} from '../../graphql/mutations/generateWorkProgressBagDetailDrilldownDownloadReport';
import ClientCartonDetailDrawer from '../../../client-cartons/components/ClientCartonDetailDrawer';
import type { ClientCartonListItem } from '../../../client-cartons/graphql/types/ClientCartonTypes';
import CentralParcelDetailDrawer from '../../../../central/parcels/components/CentralParcelDetailDrawer';
import ParcelsDownloadModal from '../../../parcels/components/ParcelsDownloadModal';
import { getUserInfo } from '../../../../../utils/auth';
import { BIG_BAG_TYPE_NO_MAWB, BIG_BAG_TYPE_WITH_MAWB, type BigBagDrilldownBagType } from '../bagType';
import type { BigBagDrilldownTitleSource } from '../bigBagDrilldownTitleSource';

/** 与 desktop-web 列表分页约定一致（参见 README：pagination.pageSize 默认 10） */
const WORK_PROGRESS_DRILLDOWN_PAGE_SIZE = 10;

export interface BigBagDrilldownContext {
  hubId: number;
  /** 大包差异 tab 下钻专用：非空时后端按 work_progress_bag_detail.pre_pickup_hub_id 过滤；包裹差异 tab 不传。 */
  prePickupHubId?: number | null;
  arrivalDateStart: string;
  arrivalDateEnd: string;
  /** 仅大包明细 client_carton 查询使用；标题仍展示 arrivalDateStart/End。 */
  clientCartonArrivalDateStart?: string | null;
  clientCartonArrivalDateEnd?: string | null;
  customerId?: number | null;
  customerName?: string | null;
  /** 有提单号场景：提单号 MAWB（对应大包明细/包裹明细 GraphQL filter.mawb） */
  mawb?: string | null;
  /** 无提单号时为筛选用大包号；有提单号时可单独收窄包裹明细（对应 GraphQL bagNumber） */
  bigBagNo?: string | null;
  /** 大包类型：1 / 0；包裹明细下钻可为 null（合并两种类型，与 KPI 汇总一致） */
  bagType: BigBagDrilldownBagType;
  isInbound?: boolean | null;
  /** 关闭大包预实差开关时，包裹明细仅限定在已入库大包内。 */
  inboundBagOnly?: boolean | null;
}

export interface BigBagProgressDrilldownDrawerProps {
  open: boolean;
  onClose: () => void;
  context: BigBagDrilldownContext | null;
  mode: 'bag' | 'parcel';
  isInbound: boolean | null;
  /** 下钻入口，用于标题 i18n（与 `wms.progress.bigBag.drilldown.titles.*` 对应） */
  titleSource: BigBagDrilldownTitleSource | null;
}

const BigBagProgressDrilldownDrawer = ({
  open,
  onClose,
  context,
  mode,
  isInbound,
  titleSource,
}: BigBagProgressDrilldownDrawerProps) => {
  const { t } = useTranslation();
  const [bagPage, setBagPage] = useState(1);
  const [bagPageSize, setBagPageSize] = useState(WORK_PROGRESS_DRILLDOWN_PAGE_SIZE);
  const [parcelPage, setParcelPage] = useState(1);
  const [parcelPageSize, setParcelPageSize] = useState(WORK_PROGRESS_DRILLDOWN_PAGE_SIZE);
  const [clientCartonDrawerVisible, setClientCartonDrawerVisible] = useState(false);
  const [selectedClientCartonId, setSelectedClientCartonId] = useState<number | null>(null);
  const [selectedClientCartonNo, setSelectedClientCartonNo] = useState<string | null>(null);
  const [parcelOrderDetailVisible, setParcelOrderDetailVisible] = useState(false);
  const [parcelOrderDetailId, setParcelOrderDetailId] = useState('');
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [downloadFileName, setDownloadFileName] = useState('');
  const [selectedParcelRowKeys, setSelectedParcelRowKeys] = useState<React.Key[]>([]);

  const {
    data: clientCartonsData,
    loading: clientCartonsLoading,
    refetch: refetchClientCartons,
  } = useQuery<GetWorkProgressClientCartonsResponse>(GET_WORK_PROGRESS_CLIENT_CARTONS, {
    variables: context
      ? {
          filter: {
            hubId: context.hubId,
            ...(context.prePickupHubId != null ? { prePickupHubId: context.prePickupHubId } : {}),
            arrivalDateStart: context.clientCartonArrivalDateStart ?? context.arrivalDateStart,
            arrivalDateEnd: context.clientCartonArrivalDateEnd ?? context.arrivalDateEnd,
            customerId: context.customerId ?? null,
            mawb: context.mawb ?? null,
            bigBagNo: context.bigBagNo ?? null,
            ...(isInbound !== null ? { isInbound } : {}),
            ...(context.inboundBagOnly ? { inboundBagOnly: true } : {}),
            page: bagPage,
            pageSize: bagPageSize,
          },
        }
      : undefined,
    skip: !open || !context || mode !== 'bag',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const cartonItems: ClientCartonListItem[] = clientCartonsData?.getWorkProgressBagDetails?.items ?? [];
  const cartonTotal: number = clientCartonsData?.getWorkProgressBagDetails?.totalCount ?? 0;

  const {
    data: parcelDetailsData,
    loading: parcelDetailsLoading,
    refetch: refetchParcelDetails,
  } = useQuery<GetWorkProgressBagParcelDetailsResponse>(GET_WORK_PROGRESS_BAG_PARCEL_DETAILS, {
    variables: context
      ? {
          filter: {
            hubId: context.hubId,
            arrivalDateStart: context.arrivalDateStart,
            arrivalDateEnd: context.arrivalDateEnd,
            bagType: context.bagType,
            customerId: context.customerId ?? null,
            mawb: context.bagType === BIG_BAG_TYPE_WITH_MAWB ? context.mawb ?? null : null,
            bagNumber: context.bigBagNo ?? null,
            ...(isInbound !== null ? { isInbound } : {}),
            ...(context.inboundBagOnly ? { inboundBagOnly: true } : {}),
            page: parcelPage,
            pageSize: parcelPageSize,
          },
        }
      : undefined,
    skip: !open || !context || mode !== 'parcel',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const parcelItems: WorkProgressBagParcelDetail[] =
    parcelDetailsData?.getWorkProgressBagParcelDetails?.content ?? [];
  const parcelTotal: number = parcelDetailsData?.getWorkProgressBagParcelDetails?.totalElements ?? 0;
  const [generateParcelDownloadReport, { loading: parcelExportLoading }] = useMutation<
    GenerateWorkProgressBagParcelDownloadReportResponse,
    GenerateWorkProgressBagParcelDownloadReportVariables
  >(GENERATE_WORK_PROGRESS_BAG_PARCEL_DOWNLOAD_REPORT);
  const [generateBagDrilldownDownloadReport, { loading: bagDrilldownExportLoading }] = useMutation<
    GenerateWorkProgressBagDetailDrilldownDownloadReportResponse,
    GenerateWorkProgressBagDetailDrilldownDownloadReportVariables
  >(GENERATE_WORK_PROGRESS_BAG_DETAIL_DRILLDOWN_DOWNLOAD_REPORT);

  React.useEffect(() => {
    if (!open) {
      setBagPage(1);
      setBagPageSize(WORK_PROGRESS_DRILLDOWN_PAGE_SIZE);
      setParcelPage(1);
      setParcelPageSize(WORK_PROGRESS_DRILLDOWN_PAGE_SIZE);
      setClientCartonDrawerVisible(false);
      setSelectedClientCartonId(null);
      setSelectedClientCartonNo(null);
      setParcelOrderDetailVisible(false);
      setParcelOrderDetailId('');
      setDownloadModalVisible(false);
      setDownloadFileName('');
      setSelectedParcelRowKeys([]);
    }
  }, [open]);

  const drawerTitle = useMemo(() => {
    const dateRangeFor = (c: BigBagDrilldownContext) =>
      c.arrivalDateStart === c.arrivalDateEnd
        ? c.arrivalDateStart
        : `${c.arrivalDateStart} ~ ${c.arrivalDateEnd}`;

    if (!context) {
      return mode === 'bag'
        ? t('wms.progress.bigBag.drilldown.diffBagsTitle', { defaultValue: '差异大包' })
        : t('wms.progress.bigBag.drilldown.diffParcelsTitle', { defaultValue: '差异包裹' });
    }

    const dateRange = dateRangeFor(context);

    if (titleSource) {
      return t(`wms.progress.bigBag.drilldown.titles.${titleSource}`, {
        dateRange,
        defaultValue: `${titleSource} — ${dateRange}`,
      });
    }

    if (mode === 'parcel') {
      const baseTitle = t('wms.progress.bigBag.drilldown.diffParcelsTitle', { defaultValue: '差异包裹' });
      return `${baseTitle} - ${dateRange}`;
    }

    const bagKindOnly =
      context.bagType === BIG_BAG_TYPE_NO_MAWB
        ? t('wms.progress.bigBag.drilldown.bagDrawer.noMawbPrefix', { defaultValue: '无提单号' })
        : context.bagType === BIG_BAG_TYPE_WITH_MAWB
          ? t('wms.progress.bigBag.drilldown.bagDrawer.withMawbPrefix', { defaultValue: '有提单号' })
          : t('wms.progress.bigBag.drilldown.bagDrawer.allBagTypesPrefix', { defaultValue: '全部类型' });

    const inboundOnly =
      isInbound === true
        ? t('wms.progress.bigBag.inbound.yes', { defaultValue: '已入库' })
        : isInbound === false
          ? t('wms.progress.bigBag.inbound.no', { defaultValue: '未入库' })
          : null;

    const headline = inboundOnly ?? bagKindOnly;
    return `${headline} - ${dateRange}`;
  }, [t, mode, context, isInbound, titleSource]);

  const cartonColumns: TableColumnsType<ClientCartonListItem> = useMemo(
    () => [
      {
        title: t('wms.clientCartons.columns.masterWaybillNo'),
        dataIndex: 'masterWaybillNo',
        key: 'masterWaybillNo',
        render: (v: string | null) => {
          return v || context?.mawb || '-';
        },
      },
      {
        title: t('wms.clientCartons.columns.cartonNo'),
        dataIndex: 'bigBagNo',
        key: 'bigBagNo',
        render: (text: string) => <span className="tabular-nums">{text}</span>,
      },
      {
        title: t('wms.progress.bigBag.columns.mawbEarliestInboundDate'),
        dataIndex: 'earliestInboundDate',
        key: 'earliestInboundDate',
        render: (v: string | null | undefined) => v || '-',
      },
      {
        title: t('wms.clientCartons.columns.customerName'),
        dataIndex: 'channelName',
        key: 'channelName',
        render: (v: string | null) => v || context?.customerName || '-',
      },
      { title: t('wms.clientCartons.columns.destinationPort'), dataIndex: 'arrivePortCode', key: 'arrivePortCode', render: (v: string | null) => v || '-' },
      {
        title: t('wms.clientCartons.columns.status'),
        dataIndex: 'scanStatus',
        key: 'scanStatus',
        render: (status: number) => (
          <Tag color={status === 1 ? 'green' : 'default'}>
            {status === 1 ? t('wms.clientCartons.status.scanned') : t('wms.clientCartons.status.notScanned')}
          </Tag>
        ),
      },
      {
        title: t('wms.clientCartons.columns.scanType'),
        dataIndex: 'scanType',
        key: 'scanType',
        render: (v: number | null) => {
          if (v === 1) return t('wms.clientCartons.scanType.batch');
          if (v === 2) return t('wms.clientCartons.scanType.individual');
          return '-';
        },
      },
      {
        title: t('wms.clientCartons.columns.scanPort'),
        dataIndex: 'scanPort',
        key: 'scanPort',
        render: (v: string | null) => v || '-',
      },
      {
        title: t('wms.clientCartons.columns.parcelCount'),
        dataIndex: 'parcelCount',
        key: 'parcelCount',
      },
      {
        title: t('wms.clientCartons.columns.onlineParcelCount'),
        dataIndex: 'onlineParcelCount',
        key: 'onlineParcelCount',
      },
      {
        title: t('wms.clientCartons.columns.offlineParcelCount'),
        dataIndex: 'offlineParcelCount',
        key: 'offlineParcelCount',
      },
      {
        title: t('wms.clientCartons.columns.createdTime'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (v: string | null) => (v ? formatISODateTime(v) : '-'),
      },
      {
        title: t('wms.clientCartons.columns.cartonScanTime'),
        dataIndex: 'scanTime',
        key: 'scanTime',
        render: (v: string | null) => (v ? formatISODateTime(v) : '-'),
      },
    ],
    [t, context?.mawb, context?.customerName]
  );

  const handleClose = () => {
    setBagPage(1);
    setBagPageSize(WORK_PROGRESS_DRILLDOWN_PAGE_SIZE);
    setParcelPage(1);
    setParcelPageSize(WORK_PROGRESS_DRILLDOWN_PAGE_SIZE);
    setSelectedParcelRowKeys([]);
    onClose();
  };

  const buildParcelExportFilter = (): WorkProgressBagParcelExportFilterInput | null => {
    if (!context) return null;
    return {
      hubId: context.hubId,
      arrivalDateStart: context.arrivalDateStart,
      arrivalDateEnd: context.arrivalDateEnd,
      bagType: context.bagType,
      customerId: context.customerId ?? null,
      mawb: context.bagType === BIG_BAG_TYPE_WITH_MAWB ? context.mawb ?? null : null,
      bagNumber: context.bigBagNo ?? null,
      ...(isInbound !== null ? { isInbound } : {}),
      ...(context.inboundBagOnly ? { inboundBagOnly: true } : {}),
    };
  };

  const selectedParcelNumbers = (): string[] => {
    const selectedKeySet = new Set(selectedParcelRowKeys.map(key => String(key)));
    return parcelItems
      .filter(item => selectedKeySet.has(String(item.id)))
      .map(item => item.parcelNumber?.trim())
      .filter((parcelNumber): parcelNumber is string => Boolean(parcelNumber));
  };

  const copySelectedParcelNumbers = async () => {
    const parcelNumbers = selectedParcelNumbers();
    if (parcelNumbers.length === 0) {
      message.warning(t('wms.progress.bigBag.messages.selectParcelToCopy', { defaultValue: 'Please select parcels to copy' }));
      return;
    }
    const text = parcelNumbers.join('\n');
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      message.success(t('common.messages.copied', { defaultValue: '复制成功' }));
    } catch (error) {
      console.error('Copy work progress bag parcel numbers failed:', error);
      message.error(t('common.messages.copyFailed', { defaultValue: '复制失败' }));
    }
  };

  const exportBagDetails = async () => {
    if (!context) return;
    const filter: WorkProgressBagDetailDrilldownExportFilterInput = {
      hubId: context.hubId,
      ...(context.prePickupHubId != null ? { prePickupHubId: context.prePickupHubId } : {}),
      arrivalDateStart: context.clientCartonArrivalDateStart ?? context.arrivalDateStart,
      arrivalDateEnd: context.clientCartonArrivalDateEnd ?? context.arrivalDateEnd,
      customerId: context.customerId ?? null,
      mawb: context.mawb ?? null,
      bigBagNo: context.bigBagNo ?? null,
      ...(isInbound !== null ? { isInbound } : {}),
      ...(context.inboundBagOnly ? { inboundBagOnly: true } : {}),
    };
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await generateBagDrilldownDownloadReport({
        variables: { filter, timezone: userTimezone },
      });
      const fileName = res.data?.generateWorkProgressBagDetailDrilldownDownloadReport;
      if (fileName) {
        setDownloadFileName(fileName);
        setDownloadModalVisible(true);
      }
    } catch (error: any) {
      console.error('Export work progress bag drilldown details failed:', error);
      message.error(error?.message || t('wms.progress.bigBag.messages.exportFailed', { defaultValue: 'Export failed, please try again later' }));
    }
  };

  const exportParcelDetails = async () => {
    const filter = buildParcelExportFilter();
    if (!filter) return;
    const parcelNumbers = selectedParcelNumbers();
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await generateParcelDownloadReport({
        variables: {
          filter: parcelNumbers.length > 0 ? { ...filter, parcelNumbers } : filter,
          timezone: userTimezone,
        },
      });
      const fileName = res.data?.generateWorkProgressBagParcelDownloadReport;
      if (fileName) {
        setDownloadFileName(fileName);
        setDownloadModalVisible(true);
      }
    } catch (error: any) {
      console.error('Export work progress bag parcel details failed:', error);
      message.error(error?.message || t('wms.progress.bigBag.messages.exportFailed', { defaultValue: 'Export failed, please try again later' }));
    }
  };

  return (
    <>
      <EditableDrawer
        title={drawerTitle}
        visible={open}
        onClose={handleClose}
        width={1200}
        showDefaultFooter={false}
        maskClosable
        zIndex={998}
      >
        {mode === 'bag' ? (
          <>
            <div className="mb-3 flex justify-end">
              <Button
                type="primary"
                loading={bagDrilldownExportLoading}
                onClick={exportBagDetails}
                disabled={!context}
              >
                <span className="font-bold">{t('wms.progress.bigBag.actions.exportBagDetails', { defaultValue: '导出' })}</span>
              </Button>
            </div>
            <Table
              rowKey="id"
              columns={cartonColumns}
              dataSource={cartonItems}
              loading={clientCartonsLoading}
            pagination={{
              current: bagPage,
              pageSize: bagPageSize,
              total: cartonTotal,
              pageSizeOptions: ['10', '20', '50', '100'],
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => t('table.pagination.totalItems', { total, defaultValue: `共 ${total} 条` }),
              onChange: (page, pageSize) => {
                const finalPageSize = pageSize ?? bagPageSize;
                setBagPage(page);
                setBagPageSize(finalPageSize);
                if (context) {
                  refetchClientCartons({
                    filter: {
                      hubId: context.hubId,
                      ...(context.prePickupHubId != null ? { prePickupHubId: context.prePickupHubId } : {}),
                      arrivalDateStart: context.clientCartonArrivalDateStart ?? context.arrivalDateStart,
                      arrivalDateEnd: context.clientCartonArrivalDateEnd ?? context.arrivalDateEnd,
                      customerId: context.customerId ?? null,
                      mawb: context.mawb ?? null,
                      bigBagNo: context.bigBagNo ?? null,
                      ...(isInbound !== null ? { isInbound } : {}),
                      ...(context.inboundBagOnly ? { inboundBagOnly: true } : {}),
                      page,
                      pageSize: finalPageSize,
                    },
                  });
                }
              },
            }}
            size="middle"
            scroll={{ x: 1100 }}
            />
          </>
        ) : (
          <>
            <div className="mb-3 flex justify-end">
              <Space>
                <Button type="primary" onClick={copySelectedParcelNumbers}>
                  <span className="font-bold">{t('wms.progress.bigBag.actions.copyParcelNumbers')}</span>
                </Button>
                <Button type="primary" loading={parcelExportLoading} onClick={exportParcelDetails}>
                  <span className="font-bold">{t('wms.progress.bigBag.actions.exportParcelDetails')}</span>
                </Button>
              </Space>
            </div>
            <Table<WorkProgressBagParcelDetail>
              rowKey="id"
              rowSelection={{
                selectedRowKeys: selectedParcelRowKeys,
                onChange: setSelectedParcelRowKeys,
              }}
              columns={[
              {
                title: t('wms.progress.bigBag.columns.parcelNumber'),
                dataIndex: 'parcelNumber',
                key: 'parcelNumber',
                width: 220,
                render: (text: string, record) => (
                  <button
                    type="button"
                    className="cursor-pointer border-0 bg-transparent p-0 text-left font-medium text-blue-600 tabular-nums hover:text-blue-800 hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!record.id?.trim()) return;
                      setParcelOrderDetailId(record.id);
                      setParcelOrderDetailVisible(true);
                    }}
                  >
                    {text}
                  </button>
                ),
              },
              { title: t('wms.progress.bigBag.columns.customerName'), dataIndex: 'customerName', key: 'customerName', width: 180 },
              { title: t('wms.progress.bigBag.columns.bagNumber'), dataIndex: 'bagNumber', key: 'bagNumber', width: 200 },
              { title: t('wms.progress.bigBag.columns.mawb'), dataIndex: 'mawb', key: 'mawb', width: 180 },
              {
                title: t('wms.progress.bigBag.columns.parcelStatus'),
                dataIndex: 'overallStatus',
                key: 'overallStatus',
                width: 120,
                render: (v: string) => {
                  const key = (v || '').trim();
                  const label = key ? t(`enum.overallStatus.${key}`, { defaultValue: key }) : '-';
                  const color =
                    key === 'DELIVERED' ? 'green' :
                    key === 'DELIVERY_FAILED' ? 'red' :
                    key === 'CANCELLED' ? 'default' :
                    'blue';
                  return <Tag color={color}>{label}</Tag>;
                },
              },
              ]}
              dataSource={parcelItems}
              loading={parcelDetailsLoading}
              pagination={{
              current: parcelPage,
              pageSize: parcelPageSize,
              total: parcelTotal,
              pageSizeOptions: ['10', '20', '50', '100'],
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => t('table.pagination.totalItems', { total, defaultValue: `共 ${total} 条` }),
              onChange: (page, pageSize) => {
                const finalPageSize = pageSize ?? parcelPageSize;
                setParcelPage(page);
                setParcelPageSize(finalPageSize);
                setSelectedParcelRowKeys([]);
                if (context) {
                  refetchParcelDetails({
                    filter: {
                      hubId: context.hubId,
                      arrivalDateStart: context.arrivalDateStart,
                      arrivalDateEnd: context.arrivalDateEnd,
                      bagType: context.bagType,
                      customerId: context.customerId ?? null,
                      mawb: context.bagType === BIG_BAG_TYPE_WITH_MAWB ? context.mawb ?? null : null,
                      bagNumber: context.bigBagNo ?? null,
                      ...(isInbound !== null ? { isInbound } : {}),
                      ...(context.inboundBagOnly ? { inboundBagOnly: true } : {}),
                      page,
                      pageSize: finalPageSize,
                    },
                  });
                }
              },
              }}
              size="middle"
              scroll={{ x: 980 }}
            />
          </>
        )}
      </EditableDrawer>

      <ParcelsDownloadModal
        visible={downloadModalVisible}
        onClose={() => setDownloadModalVisible(false)}
        fileName={downloadFileName}
      />

      <ClientCartonDetailDrawer
        visible={clientCartonDrawerVisible}
        clientCartonNo={selectedClientCartonNo}
        id={selectedClientCartonId}
        onClose={() => setClientCartonDrawerVisible(false)}
      />

      <CentralParcelDetailDrawer
        showInterceptionExemptionAlert={true}
        visible={parcelOrderDetailVisible}
        orderId={parcelOrderDetailId}
        userRole={getUserInfo()?.account?.role?.name || ''}
        onClose={() => setParcelOrderDetailVisible(false)}
        showExternalTrack
      />
    </>
  );
};

export default BigBagProgressDrilldownDrawer;

